import BubbleChatIcon from "@hugeicons/core-free-icons/BubbleChatIcon";
import LayoutGridIcon from "@hugeicons/core-free-icons/LayoutGridIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import { useRouterState } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import {
	shouldShowDock,
	shouldShowShell,
} from "@/shared/config/routes/route-detection";
import { cn } from "@/shared/lib/utils";
import { AppHeaderCenterSlot } from "@/shared/ui/app-header-slots";
import { Card, CardContent, CardHeader } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { CalendarSkeleton } from "@/shared/ui/skeletons/calendar-skeleton";
import { DashboardSkeleton } from "@/shared/ui/skeletons/dashboard-skeleton";

const FORM_ROW_IDS = ["one", "two", "three", "four"] as const;
const CARD_IDS = ["primary", "secondary", "tertiary"] as const;
const ADMIN_TAB_IDS = [
	"overview",
	"waiting",
	"organizations",
	"members",
	"system",
	"feedback",
	"balance",
	"plans",
] as const;
const DOC_LINE_IDS = ["intro", "setup", "usage", "details", "footer"] as const;
const ACTIVITY_CARD_IDS = ["one", "two", "three", "four"] as const;
const FIVE_ROW_IDS = [...FORM_ROW_IDS, "five"] as const;
const SIX_ROW_IDS = [...FORM_ROW_IDS, "five", "six"] as const;
const DEMO_SECTION_IDS = ["actions", "surfaces", "bento", "forms"] as const;

const APP_SHELL_STYLE = {
	"--sidebar-width": "24rem",
	"--sidebar-width-icon": "3rem",
	contain: "layout style size",
	height: "100%",
} as CSSProperties;

const PLAYGROUND_VIEWPORT_STYLE = {
	marginTop: "var(--impersonation-banner-h, 0px)",
	height: "100%",
} satisfies CSSProperties;

function SidebarFrameSkeleton() {
	return (
		<aside
			aria-hidden="true"
			className="group peer hidden text-sidebar-foreground md:block"
			data-collapsible=""
			data-side="left"
			data-slot="sidebar"
			data-state="expanded"
			data-variant="sidebar"
		>
			<div className="relative w-[min(var(--sidebar-width),100vw)] bg-transparent transition-[width] duration-moderate ease-out" />
			<div className="fixed inset-y-0 start-0 z-10 flex w-[min(var(--sidebar-width),100vw)] transition-[inset,width] duration-moderate ease-out">
				<div
					className="relative flex h-full max-h-screen w-full flex-col border-sidebar-border border-e bg-sidebar"
					data-sidebar="sidebar"
					data-slot="sidebar-inner"
					data-surface-level="2"
				>
					{/* Header mirrors the real SidebarHeader on the default "Widgets"
					    tab: brand + profile + the Widgets|Chat toggle. The chat-only
					    conversation combobox is NOT rendered here — it only exists on
					    the chat tab, so showing it on the default surface was the main
					    reason the old skeleton looked nothing like the loaded sidebar. */}
					<header className="relative border-border/30 border-b bg-gradient-to-b from-sidebar via-sidebar to-sidebar/95 px-3 py-2">
						<div className="relative flex items-center gap-2">
							<div className="flex shrink-0 items-center gap-2">
								<span className="bavard-word text-[21px] text-foreground leading-none">
									Bavard
								</span>
							</div>
							<div className="flex min-w-0 flex-1 items-center justify-end gap-2">
								<Skeleton className="size-7 rounded-full" />
							</div>
						</div>
						<div className="mt-2 flex items-center gap-1.5">
							<div className="flex flex-1 rounded-lg bg-muted/40 p-0.5 ring-1 ring-border/20">
								<div className="relative flex flex-1 items-center justify-center gap-1 rounded-md bg-surface-current px-2 py-1.5 font-medium text-[11px] text-foreground shadow-foreground/5 shadow-sm">
									<HugeiconsIcon
										aria-hidden="true"
										className="size-3 text-primary"
										icon={LayoutGridIcon}
									/>
									Widgets
								</div>
								<div className="relative flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 font-medium text-[11px] text-muted-foreground">
									<HugeiconsIcon
										aria-hidden="true"
										className="size-3"
										icon={BubbleChatIcon}
									/>
									Chat
								</div>
							</div>
						</div>
					</header>
					{/* Widgets-tab body: the calendar panel currently ships no
					    widgets (the prayer-times group was removed with the
					    react-prayer-widget package), so the loaded body is empty
					    chrome — the skeleton matches that exactly rather than
					    inventing rows that never arrive. */}
					<div className="flex min-h-0 flex-1 flex-col bg-sidebar p-0" />
				</div>
			</div>
		</aside>
	);
}

function PendingSidebarInset({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<main
			className={cn(
				"relative flex w-full flex-1 flex-col bg-background",
				className,
			)}
			data-slot="sidebar-inset"
			id="main-content"
		>
			{children}
		</main>
	);
}

/**
 * The navbar, as a shape.
 *
 * NOT the real `AppNavbar`: this renders while `AppProviders` is still in
 * flight, so there is no session, no query client and no theme bridge — and the
 * startup-boundary guard forbids this module from importing `@/widgets/*`
 * anyway. It is the same 60px bar with the same hairline and the same wordmark
 * in the same place, so the real one replaces it without the frame moving.
 *
 * The wordmark is real text rather than a grey block: it is the one thing on
 * the bar that does not depend on who is logged in, and a skeleton that can
 * show the actual content instead of a placeholder should.
 */
function NavbarFrameSkeleton() {
	return (
		<div
			aria-hidden="true"
			className="flex h-[var(--app-navbar-h)] flex-none items-center justify-between border-[color:var(--brand-hairline)] border-b bg-[var(--brand-ground)] px-5"
			dir="ltr"
		>
			<span className="bavard-word text-[24px] text-[color:var(--brand-ink-cool)] leading-none">
				Bavard
			</span>
			<div className="flex items-center gap-2">
				<div className="h-[34px] w-[74px] rounded-[10px] bg-[var(--brand-soft)]" />
				<div className="size-[34px] rounded-[10px] bg-[var(--brand-soft)]" />
				<div className="size-[34px] rounded-[10px] bg-[var(--brand-soft)]" />
			</div>
		</div>
	);
}

/**
 * The app frame during a cold load: navbar on top, rail gutter reserved at the
 * inline-start edge, page skeleton in the rest.
 *
 * This used to draw only a 24rem sidebar and the page beneath it — the shell as
 * it looked before the navbar and rail replaced that arrangement. So a refresh
 * put up a frame the app no longer has, then swapped the whole screen for the
 * real one, which is what read as a skeleton "flashing".
 */
function AppShellFrameSkeleton({
	children,
	reserveBottomNav = true,
	reserveRail = true,
	showSidebar = true,
}: {
	children: ReactNode;
	reserveBottomNav?: boolean;
	reserveRail?: boolean;
	showSidebar?: boolean;
}) {
	return (
		<div
			className="group/sidebar-wrapper flex size-full min-h-[100dvh] flex-col overflow-hidden bg-background text-foreground"
			data-slot="sidebar-wrapper"
			style={APP_SHELL_STYLE}
		>
			<NavbarFrameSkeleton />
			<div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
				{showSidebar ? <SidebarFrameSkeleton /> : null}
				<div
					className={cn(
						"flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
						reserveRail && "md:ps-[var(--app-rail-w)]",
						reserveBottomNav ? "pb-[var(--bottom-nav-height)] md:pb-0" : "pb-0",
					)}
				>
					{children}
				</div>
			</div>
		</div>
	);
}

function CalendarPageSkeleton() {
	return (
		<PendingSidebarInset className="h-full min-h-0 overflow-hidden pb-0">
			<div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-2 pt-1">
				<CalendarSkeleton />
			</div>
		</PendingSidebarInset>
	);
}

function ActivityPageSkeleton() {
	// Real /activity is the Notifications page: All/Unread tabs followed by a
	// dated group of bordered notification cards.
	const cards = ACTIVITY_CARD_IDS;
	return (
		<PendingSidebarInset>
			<div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
				<div className="mx-auto w-full max-w-3xl">
					<div className="mb-4 flex gap-4">
						<Skeleton className="h-5 w-10" />
						<Skeleton className="h-5 w-16" />
					</div>
					<div
						className="rounded-xl border bg-surface-2 p-2"
						data-surface-level="2"
					>
						<Skeleton className="ms-1 mt-1 mb-2 h-3 w-16" />
						<div className="space-y-2">
							{cards.map((cardId) => (
								<div className="rounded-lg border p-3" key={cardId}>
									<div className="flex items-start gap-3">
										<Skeleton className="size-8 shrink-0 rounded-md" />
										<div className="flex flex-1 flex-col gap-1.5">
											<Skeleton className="h-3 w-28" />
											<Skeleton className="h-4 w-40" />
											<Skeleton className="h-3 w-48 max-w-full" />
											<Skeleton className="h-3 w-24" />
										</div>
										<Skeleton className="h-3 w-14 shrink-0" />
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</PendingSidebarInset>
	);
}

function BillingPageSkeleton() {
	// Real billing renders three cards: Subscription (col 1) + Credits (col 2)
	// + Usage (col 2, taller). No invoice-history card on this surface.
	return (
		<div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
			<header className="mb-6">
				<Skeleton className="h-8 w-40" />
				<Skeleton className="mt-2 h-4 w-72 max-w-full" />
			</header>
			<div className="grid gap-6 lg:grid-cols-2">
				<Card id="subscription">
					<CardHeader>
						<Skeleton className="h-5 w-40" />
						<Skeleton className="h-4 w-64 max-w-full" />
					</CardHeader>
					<CardContent className="space-y-3">
						<Skeleton className="h-9 w-48" />
						<Skeleton className="h-4 w-56 max-w-full" />
					</CardContent>
				</Card>
				<Card id="credits">
					<CardHeader>
						<Skeleton className="h-5 w-40" />
						<Skeleton className="h-4 w-64 max-w-full" />
					</CardHeader>
					<CardContent className="space-y-3">
						<Skeleton className="h-9 w-24" />
						<Skeleton className="h-4 w-32" />
					</CardContent>
				</Card>
				<Card className="lg:col-start-2" id="usage">
					<CardHeader>
						<Skeleton className="h-5 w-32" />
						<Skeleton className="h-4 w-56 max-w-full" />
					</CardHeader>
					<CardContent className="space-y-3">
						{FORM_ROW_IDS.map((rowId) => (
							<div className="flex items-center justify-between" key={rowId}>
								<Skeleton className="h-4 w-32" />
								<Skeleton className="h-4 w-16" />
							</div>
						))}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

function MembersPageSkeleton() {
	const rows = SIX_ROW_IDS;
	return (
		<div className="h-dvh overflow-y-auto bg-background text-foreground">
			<div className="flex flex-col gap-4 p-4 md:p-8">
				<div className="mx-auto w-full max-w-7xl space-y-6 md:space-y-0">
					<div className="space-y-2">
						<Skeleton className="h-8 w-48" />
						<Skeleton className="h-4 w-80 max-w-full" />
					</div>
					<div
						className="rounded-xl border bg-surface-2 p-4"
						data-surface-level="2"
					>
						<div className="mb-4 flex items-center justify-between gap-3">
							<Skeleton className="h-9 w-72 max-w-full" />
							<Skeleton className="h-9 w-28" />
						</div>
						<div className="space-y-2">
							{rows.map((rowId) => (
								<Skeleton className="h-12 w-full" key={rowId} />
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function RolesPageSkeleton() {
	return (
		<div className="relative h-dvh overflow-y-auto bg-background text-foreground">
			<div className="flex flex-col gap-4 p-4 md:p-8">
				<div className="mx-auto w-full max-w-7xl space-y-6">
					<div
						className="rounded-xl border bg-surface-2 p-4"
						data-surface-level="2"
					>
						<div className="mb-4 flex items-center justify-between gap-3">
							<Skeleton className="h-8 w-48" />
							<Skeleton className="h-8 w-36" />
						</div>
						<div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
							<div className="space-y-2">
								{FORM_ROW_IDS.map((rowId) => (
									<Skeleton className="h-11 w-full" key={rowId} />
								))}
							</div>
							<div className="space-y-3">
								<Skeleton className="h-10 w-full" />
								<Skeleton className="h-64 w-full" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function OrgOverviewPageSkeleton() {
	return (
		<div className="h-dvh overflow-y-auto bg-background text-foreground">
			<div className="mx-auto flex w-full max-w-5xl flex-col gap-8 p-4 md:p-8">
				<header className="space-y-1.5">
					<Skeleton className="h-3 w-28" />
					<Skeleton className="h-9 w-72 max-w-full" />
					<Skeleton className="h-4 w-24" />
				</header>
				<section className="space-y-3">
					<Skeleton className="h-5 w-16" />
					<div className="grid gap-3 sm:grid-cols-3">
						{CARD_IDS.map((cardId) => (
							<Skeleton className="h-24 w-full rounded-xl" key={cardId} />
						))}
					</div>
				</section>
				<section className="space-y-3">
					<Skeleton className="h-5 w-20" />
					<Skeleton className="h-24 w-full rounded-xl" />
				</section>
				<section className="grid gap-3 sm:grid-cols-2">
					<Skeleton className="h-24 w-full rounded-xl" />
					<Skeleton className="h-24 w-full rounded-xl" />
				</section>
			</div>
		</div>
	);
}

function ConfigPageSkeleton() {
	return (
		<div className="mx-auto flex w-full max-w-7xl flex-1 gap-6 p-4 md:p-6">
			<aside className="hidden w-56 shrink-0 space-y-2 md:block">
				{SIX_ROW_IDS.map((rowId) => (
					<Skeleton className="h-9 w-full rounded-lg" key={rowId} />
				))}
			</aside>
			<section className="min-w-0 flex-1 space-y-4">
				<Skeleton className="h-8 w-56" />
				<Skeleton className="h-4 w-96 max-w-full" />
				{FORM_ROW_IDS.map((rowId) => (
					<Skeleton className="h-20 w-full rounded-xl" key={rowId} />
				))}
			</section>
		</div>
	);
}

function DashboardPageSkeleton() {
	return (
		<PendingSidebarInset>
			<div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
				<div className="mx-auto w-full max-w-7xl">
					<DashboardSkeleton />
				</div>
			</div>
		</PendingSidebarInset>
	);
}

function AdminPageSkeleton() {
	return (
		<div className="h-dvh overflow-y-auto bg-background text-foreground">
			<div className="flex flex-col gap-4 p-4 md:p-8 md:pt-3">
				<div className="mx-auto w-full max-w-7xl space-y-6">
					<header className="space-y-5 md:sr-only">
						<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
							<div className="space-y-1.5">
								<Skeleton className="h-4 w-24" />
								<Skeleton className="h-3 w-24" />
								<Skeleton className="h-10 w-72 max-w-full" />
								<Skeleton className="h-4 w-80 max-w-full" />
							</div>
							<Skeleton className="h-9 w-44" />
						</div>
						<div className="h-px bg-linear-to-r from-transparent via-border/60 to-transparent" />
					</header>
					<div className="mb-6 flex h-auto w-full items-stretch gap-1.5 overflow-x-auto rounded-xl bg-muted/30 p-1.5 ring-1 ring-border/30 sm:gap-2 sm:p-2">
						{ADMIN_TAB_IDS.map((tabId) => (
							<Skeleton
								className="h-9 min-w-24 flex-1 rounded-lg"
								key={tabId}
							/>
						))}
					</div>
					<AdminTabPanelSkeleton />
				</div>
			</div>
		</div>
	);
}

export function AdminTabPanelSkeleton() {
	return (
		<div className="rounded-xl border bg-surface-2 p-4" data-surface-level="2">
			<div className="mb-4 flex items-center justify-between gap-3">
				<div className="space-y-2">
					<Skeleton className="h-6 w-44" />
					<Skeleton className="h-4 w-72 max-w-full" />
				</div>
				<Skeleton className="h-9 w-32" />
			</div>
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				{ADMIN_TAB_IDS.slice(0, 4).map((tabId) => (
					<Skeleton className="h-24 rounded-xl" key={tabId} />
				))}
			</div>
			<Skeleton className="mt-4 h-72 w-full rounded-xl" />
		</div>
	);
}

function PlaygroundPageSkeleton() {
	const rows = FIVE_ROW_IDS;
	return (
		<div
			className="flex min-h-0 flex-col gap-4 overflow-hidden p-4 sm:p-6 md:pt-3"
			style={PLAYGROUND_VIEWPORT_STYLE}
		>
			<header className="space-y-1 md:sr-only">
				<Skeleton className="h-7 w-40" />
				<Skeleton className="h-4 w-[38rem] max-w-full" />
				<Skeleton className="h-4 w-[28rem] max-w-full" />
			</header>
			<div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[1fr] gap-4 lg:grid-cols-[22rem_1fr] xl:grid-cols-[24rem_1fr]">
				<aside
					className="hidden min-h-0 rounded-xl border bg-surface-2 p-4 lg:block"
					data-surface-level="2"
				>
					<Skeleton className="h-6 w-40" />
					<div className="mt-5 space-y-3">
						{rows.map((rowId) => (
							<Skeleton className="h-10 w-full" key={rowId} />
						))}
					</div>
				</aside>
				<section
					className="flex min-h-0 flex-col rounded-xl border bg-surface-2 p-3"
					data-surface-level="2"
				>
					<div className="mb-3 flex gap-2">
						{CARD_IDS.map((cardId) => (
							<Skeleton className="h-8 w-24" key={cardId} />
						))}
					</div>
					<Skeleton className="min-h-0 flex-1 rounded-lg" />
					<Skeleton className="mt-3 h-12 w-full rounded-lg" />
				</section>
			</div>
		</div>
	);
}

function LandingPageSkeleton() {
	return (
		<div className="min-h-screen bg-background px-5 py-5 text-foreground">
			<nav className="mx-auto flex max-w-7xl items-center justify-between gap-4">
				<Skeleton className="h-9 w-36 rounded-full" />
				<div className="hidden items-center gap-2 md:flex">
					{FORM_ROW_IDS.map((rowId) => (
						<Skeleton className="h-8 w-20 rounded-full" key={rowId} />
					))}
				</div>
				<Skeleton className="h-9 w-28 rounded-full" />
			</nav>
			<section className="mx-auto grid max-w-7xl gap-10 py-16 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)] lg:py-24">
				<div className="space-y-6">
					<Skeleton className="h-4 w-40 rounded-full" />
					<Skeleton className="h-16 w-full max-w-2xl rounded-xl" />
					<Skeleton className="h-16 w-4/5 max-w-xl rounded-xl" />
					<div className="flex flex-wrap gap-3">
						<Skeleton className="h-11 w-36 rounded-full" />
						<Skeleton className="h-11 w-32 rounded-full" />
					</div>
				</div>
				<div
					className="min-h-80 rounded-3xl border bg-surface-2 p-4"
					data-surface-level="2"
				>
					<Skeleton className="h-full min-h-72 w-full rounded-2xl" />
				</div>
			</section>
		</div>
	);
}

function DemoPageSkeleton() {
	const demoSectionIds = DEMO_SECTION_IDS;
	return (
		<div className="flex min-h-[100dvh] w-full flex-col bg-black p-8 text-white">
			<div className="relative max-w-[800px] rounded-[32px] border border-white/10 bg-white/5 p-6">
				<Skeleton className="h-8 w-28 bg-white/15" />
				<div className="mt-8 space-y-8">
					{demoSectionIds.map((sectionId) => (
						<section className="space-y-5" key={sectionId}>
							<div className="flex items-center gap-3">
								<Skeleton className="h-3 w-36 bg-white/15" />
								<div className="h-px flex-1 bg-white/10" />
							</div>
							<div className="flex flex-wrap gap-6">
								<Skeleton className="h-24 w-48 bg-white/15" />
								<Skeleton className="h-24 w-48 bg-white/15" />
								<Skeleton className="h-24 w-48 bg-white/15" />
							</div>
						</section>
					))}
				</div>
			</div>
		</div>
	);
}

/*
 * The docs, as a shape: nav bar, sidebar, article, table of contents.
 *
 * It used to be a centred `max-w-4xl` article on a blank page — the right idea
 * for a blog, but the docs are a THREE-COLUMN layout with a fixed top bar, so
 * the pending state drew one thing and the loaded page replaced it with
 * another. The grid tracks below are the same `--docs-sidebar-w` /
 * `--docs-toc-w` the real `.docs-shell` uses, so the columns land where the
 * content is about to.
 *
 * Deliberately built from `.docs-*` class names rather than the primitives:
 * this module may not import `@/pages/*` (the startup-boundary guard), and
 * `docs.css` is a global stylesheet, so the classes are reachable while the
 * page's own chunk is not.
 */
function DocsPageSkeleton() {
	return (
		<div className="docs-root min-h-[100dvh]">
			<div className="docs-nav">
				<div className="docs-nav-left">
					<Skeleton className="h-6 w-24" />
				</div>
				<Skeleton className="h-8 flex-1 rounded-lg" />
				<div className="docs-nav-right">
					<Skeleton className="h-[34px] w-[74px] rounded-[10px]" />
					<Skeleton className="size-[34px] rounded-[10px]" />
					<Skeleton className="h-[34px] w-24 rounded-[10px]" />
				</div>
			</div>
			<div className="docs-shell">
				{/* Sidebar: two collapsed groups over a run of nested links. */}
				<aside className="docs-sidebar space-y-2">
					{SIX_ROW_IDS.map((rowId, index) => (
						<Skeleton
							className={cn(
								"h-7 rounded-md",
								index % 3 === 0 ? "w-40" : "ms-3 w-32",
							)}
							key={rowId}
						/>
					))}
				</aside>
				<main className="docs-main">
					<div className="docs-page">
						{/* Article header — title then description, matching
						    `.docs-article-title`'s clamp and the lede under it. */}
						<Skeleton className="h-11 w-[22rem] max-w-full" />
						<Skeleton className="mt-3 h-4 w-[28rem] max-w-full" />
						<Skeleton className="mt-2 h-4 w-[24rem] max-w-full" />
						{/* The hero card every doc page opens with. */}
						<Skeleton className="mt-7 h-56 w-full rounded-2xl" />
						<div className="mt-8 space-y-6">
							{DOC_LINE_IDS.map((lineId) => (
								<div className="space-y-2" key={lineId}>
									<Skeleton className="h-5 w-48" />
									<Skeleton className="h-4 w-full" />
									<Skeleton className="h-4 w-4/5" />
								</div>
							))}
						</div>
					</div>
				</main>
				<aside className="docs-toc-col space-y-2">
					<Skeleton className="h-3 w-24" />
					{FIVE_ROW_IDS.map((rowId, index) => (
						<Skeleton
							className={cn("h-4", index % 2 === 0 ? "w-32" : "ms-3 w-24")}
							key={rowId}
						/>
					))}
				</aside>
			</div>
		</div>
	);
}

function ContactsPageSkeleton() {
	const rows = SIX_ROW_IDS;
	return (
		<div className="container mx-auto px-4 py-6">
			<div className="space-y-4">
				<div className="flex items-center justify-between gap-3">
					<div className="space-y-2">
						<Skeleton className="h-8 w-44" />
						<Skeleton className="h-4 w-72 max-w-full" />
					</div>
					<Skeleton className="h-9 w-32" />
				</div>
				<div
					className="rounded-xl border bg-surface-2 p-4"
					data-surface-level="2"
				>
					<div className="mb-4 flex gap-2">
						<Skeleton className="h-9 w-64 max-w-full" />
						<Skeleton className="h-9 w-24" />
					</div>
					<div className="space-y-2">
						{rows.map((rowId) => (
							<Skeleton className="h-12 w-full" key={rowId} />
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

function AuthFlowSkeleton() {
	return (
		<div className="flex min-h-dvh items-center justify-center bg-background p-4">
			<div className="w-full max-w-md space-y-5 rounded-2xl border bg-surface-2 p-6 shadow-sm">
				<div className="flex flex-col items-center gap-3">
					<Skeleton className="size-12 rounded-xl" />
					<Skeleton className="h-7 w-48" />
					<Skeleton className="h-4 w-64 max-w-full" />
				</div>
				{FORM_ROW_IDS.map((rowId) => (
					<Skeleton className="h-10 w-full rounded-lg" key={rowId} />
				))}
				<Skeleton className="h-10 w-full rounded-lg" />
			</div>
		</div>
	);
}

function AccountPageSkeleton() {
	return (
		<div className="mx-auto w-full max-w-5xl space-y-6 p-4 md:p-8">
			<Skeleton className="h-8 w-48" />
			<div className="grid gap-4 lg:grid-cols-2">
				{CARD_IDS.map((cardId) => (
					<Skeleton className="h-52 w-full rounded-xl" key={cardId} />
				))}
			</div>
		</div>
	);
}

/**
 * /documents while the route chunk loads.
 *
 * It used to draw a 288px left rail of six rows beside a card — a master/detail
 * layout the documents page has never had. What actually arrives is a single
 * full-bleed canvas panel (`DocumentsSectionLayout`), with the contact form as
 * an optional strip ABOVE it and nothing at all down the side, so the rail slid
 * away and the canvas jumped the full width of it on every visit.
 *
 * One panel on the page's own padding, matching `pages/documents`'s Suspense
 * fallback. No contact strip: this skeleton renders before the route resolves,
 * and on the ordinary visit — no `?phoneE164` — the real page has no strip.
 */
function DocumentsSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-4 sm:py-4">
			<Skeleton className="min-h-0 w-full flex-1 rounded-lg" />
		</div>
	);
}

function selectRouteSkeleton(pathname: string) {
	if (pathname === "/") {
		return <LandingPageSkeleton />;
	}
	if (pathname === "/login") {
		return <AuthFlowSkeleton />;
	}
	if (pathname.startsWith("/accept-invitation/")) {
		return <AuthFlowSkeleton />;
	}
	if (pathname === "/pending-org") {
		return <AuthFlowSkeleton />;
	}
	if (pathname === "/onboarding/checkout") {
		return <AuthFlowSkeleton />;
	}
	if (pathname === "/onboarding/success") {
		return <AuthFlowSkeleton />;
	}
	if (pathname === "/calendar") {
		return <CalendarPageSkeleton />;
	}
	if (pathname === "/dashboard") {
		return <DashboardPageSkeleton />;
	}
	if (pathname === "/activity") {
		return <ActivityPageSkeleton />;
	}
	if (pathname === "/billing") {
		return <BillingPageSkeleton />;
	}
	if (pathname === "/contacts") {
		return <ContactsPageSkeleton />;
	}
	if (pathname === "/members") {
		return <MembersPageSkeleton />;
	}
	if (pathname === "/org") {
		return <OrgOverviewPageSkeleton />;
	}
	if (pathname === "/roles") {
		return <RolesPageSkeleton />;
	}
	if (pathname === "/account") {
		return <AccountPageSkeleton />;
	}
	if (pathname === "/config") {
		return <ConfigPageSkeleton />;
	}
	if (pathname === "/superuser") {
		return <AdminPageSkeleton />;
	}
	if (pathname === "/playground") {
		return <PlaygroundPageSkeleton />;
	}
	if (pathname === "/demo") {
		return <DemoPageSkeleton />;
	}
	if (pathname === "/documents") {
		return <DocumentsSkeleton />;
	}
	if (pathname.startsWith("/docs")) {
		return <DocsPageSkeleton />;
	}
	return <CalendarPageSkeleton />;
}

export function CurrentRouteSkeleton() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const content = selectRouteSkeleton(pathname);
	const showShell = shouldShowShell(pathname);
	const showDock = shouldShowDock(pathname);
	const showSidebar =
		showShell &&
		!pathname.startsWith("/dashboard") &&
		pathname !== "/documents";
	const hasPageOwnedCenter =
		pathname === "/config" ||
		pathname === "/account" ||
		pathname === "/playground" ||
		pathname.startsWith("/superuser");
	const pendingCenterSlot = hasPageOwnedCenter ? (
		<AppHeaderCenterSlot>
			{pathname === "/config" ? (
				<Skeleton className="h-4 w-40 rounded-md" />
			) : null}
		</AppHeaderCenterSlot>
	) : null;

	if (showShell || showDock) {
		return (
			<output aria-busy="true" aria-live="polite" className="block min-h-full">
				<span className="sr-only">Loading page</span>
				{pendingCenterSlot}
				<AppShellFrameSkeleton
					reserveBottomNav={showShell}
					// The calendar's own sidebar occupies that edge, so it gets no
					// rail — the same rule `ShellLayout` applies to the real chrome.
					reserveRail={pathname !== "/calendar"}
					showSidebar={showSidebar}
				>
					{content}
				</AppShellFrameSkeleton>
			</output>
		);
	}

	return (
		<output aria-busy="true" aria-live="polite" className="block min-h-full">
			<span className="sr-only">Loading page</span>
			{content}
		</output>
	);
}
